import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const OwnerPriceSettings = ({ ownerId }) => {
  const [pricePerHour, setPricePerHour] = useState(5.00);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (ownerId) {
      loadSettings();
    }
  }, [ownerId]);

  const loadSettings = async () => {
    try {
      const settingsRef = doc(db, 'owner-settings', ownerId);
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        setPricePerHour(settingsDoc.data().pricePerHour || 5.00);
      } else {
        // Create default settings
        await setDoc(settingsRef, { pricePerHour: 5.00 });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Validate price
    const price = parseFloat(pricePerHour);
    if (isNaN(price) || price < 0) {
      setMessage({ type: 'error', text: 'Price must be a positive number' });
      setSaving(false);
      return;
    }
    if (price > 100) {
      setMessage({ type: 'error', text: 'Price cannot exceed $100 per hour' });
      setSaving(false);
      return;
    }

    try {
      const settingsRef = doc(db, 'owner-settings', ownerId);
      await setDoc(settingsRef, { 
        pricePerHour: price,
        updatedAt: new Date().toISOString()
      });
      setMessage({ type: 'success', text: 'Pricing updated successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const calculateExamples = () => {
    const price = parseFloat(pricePerHour);
    return [
      { time: '30 minutes', price: price * 0.5 },
      { time: '1 hour', price: price },
      { time: '2 hours', price: price * 2 },
      { time: '4 hours', price: price * 4 },
      { time: '8 hours (All day)', price: price * 8 },
      { time: '24 hours', price: price * 24 },
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Pricing Settings</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Price Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price per Hour (in dollars)
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                step="0.25"
                min="0"
                max="100"
                value={pricePerHour}
                onChange={(e) => setPricePerHour(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-3 sm:text-sm border-gray-300 rounded-md py-3 border"
                placeholder="5.00"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Set your hourly parking rate. Customers will be charged based on this rate.
            </p>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className={`mt-6 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
                saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>

            {/* Messages */}
            {message.text && (
              <div className={`mt-4 p-4 rounded-md ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.text}
              </div>
            )}
          </div>

          {/* Pricing Preview */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Pricing Preview</h3>
            <div className="space-y-2">
              {calculateExamples().map((example, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">{example.time}</span>
                  <span className="text-lg font-bold text-gray-900">
                    ${example.price.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-gray-500">
              * Platform fees will be deducted from these amounts based on admin settings
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Pricing Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Research competitive rates in your area</li>
                <li>Consider offering daily maximums for long-term parking</li>
                <li>Adjust prices based on demand and location</li>
                <li>Remember that platform fees will be deducted from payments</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerPriceSettings;